import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Tag } from 'lucide-react';
import SEO from '../components/SEO/SEO';
import { fetchResource, fetchRelatedResources } from '../services/resourcesApi';
import { Resource } from '../models/Resource';
import styles from './ResourceDetail.module.css';

const ResourceDetail = () => {
    const { slug } = useParams();
    const [resource, setResource] = useState(null);
    const [relatedResources, setRelatedResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadResource = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const resourceData = await fetchResource(slug);
                const resourceInstance = new Resource(resourceData);
                setResource(resourceInstance);

                // Load related resources if there are tags
                if (resourceInstance.tags.length >= 2) {
                    const related = await fetchRelatedResources(
                        resourceInstance.tags,
                        resourceInstance.id,
                        4 // Limit to 4 related articles
                    );
                    setRelatedResources(related);
                }
            } catch (err) {
                console.error('Failed to load resource:', err);
                setError(err);
                setResource(null);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            loadResource();
        }
    }, [slug]);

    if (loading) {
        return (
            <>
                <SEO
                    title="Loading Resource"
                    description="Loading resource content..."
                    noindex={true}
                />
                <div className={styles.container}>
                    <div className={styles.loading}>
                        <div className={styles.spinner}></div>
                        <p>Loading resource...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <SEO
                    title="Resource Not Found"
                    description="The requested resource could not be found."
                    noindex={true}
                />
                <div className={styles.container}>
                    <div className={styles.error}>
                        <h2>Resource Not Found</h2>
                        <p>The resource you're looking for could not be found.</p>
                        <Link to="/resources" className={styles.backLink}>
                            <ChevronLeft size={16} />
                            Back to Resources
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    if (!resource) {
        const title = `Resource - ${slug}`;
        const description = `Resource page for ${slug}. Individual resource pages are currently being developed.`;
        
        return (
            <>
                <SEO
                    title={title}
                    description={description}
                    noindex={true}
                />
                <div className={styles.container}>
                    <div className={styles.comingSoon}>
                        <Link to="/resources" className={styles.backLink}>
                            <ChevronLeft size={16} />
                            Back to Resources
                        </Link>
                        
                        <div className={styles.comingSoonContent}>
                            <h1>Resource Detail Page</h1>
                            <div className={styles.comingSoonBox}>
                                <h2>Coming Soon</h2>
                                <p>Individual resource pages are currently being developed.</p>
                                <p>Resource slug: <code>{slug}</code></p>
                            </div>
                            <p>For now, please browse our resources from the main resources page.</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // Generate SEO data for the resource
    const seoData = {
        title: resource.title,
        description: resource.excerpt,
        keywords: `${resource.tags.join(', ')}, ${resource.category.name}, ABC Railing`,
        canonical: `${window.location.origin}${resource.getUrl()}`,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": resource.title,
            "description": resource.excerpt,
            "image": "/logo192.png",
            "author": {
                "@type": "Organization",
                "name": "ABC Railing"
            },
            "publisher": {
                "@type": "Organization",
                "name": "ABC Railing",
                "logo": {
                    "@type": "ImageObject",
                    "url": `${window.location.origin}/logo192.png`
                }
            },
            "datePublished": resource.createdAt.toISOString(),
            "dateModified": resource.updatedAt.toISOString(),
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${window.location.origin}${resource.getUrl()}`
            },
            "articleSection": resource.category.name,
            "keywords": resource.tags.join(', ')
        }
    };

    return (
        <>
            <SEO
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                canonical={seoData.canonical}
                structuredData={seoData.structuredData}
            />
            <div className={styles.container}>
                <div className={styles.header}>
                    <Link to="/resources" className={styles.backLink}>
                        <ChevronLeft size={16} />
                        Back to Resources
                    </Link>

                    <div className={styles.metadata}>
                        <div className={styles.category}>
                            <span>{resource.category.name}</span>
                        </div>
                        <div className={styles.date}>
                            <Calendar size={14} />
                            <span>{resource.getFormattedUpdatedDate()}</span>
                        </div>
                    </div>
                </div>

                <article className={styles.article}>
                    <header className={styles.articleHeader}>
                        <h1>{resource.title}</h1>
                        {resource.featured && (
                            <div className={styles.featuredBadge}>
                                Featured
                            </div>
                        )}
                    </header>

                    <div className={styles.excerpt}>
                        <p>{resource.excerpt}</p>
                    </div>

                    <div className={styles.content}>
                        <div className={styles.tableResponsive}>
                            <div dangerouslySetInnerHTML={{ __html: resource.content }} />
                        </div>
                    </div>

                    {resource.tags.length > 0 && (
                        <div className={styles.tags}>
                            <h3><Tag size={16} style={{ marginRight: '8px' }} /> Tags</h3>
                            <div className={styles.tagList}>
                                {resource.tags.map((tag, index) => (
                                    <span key={index} className={styles.tag}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {relatedResources.length > 0 && (
                        <div className={styles.relatedResources}>
                            <h2>You Might Also Like</h2>
                            <div className={styles.relatedResourcesGrid}>
                                {relatedResources.map((related) => (
                                    <div key={related.id} className={styles.relatedResourceCard}>
                                        <Link to={related.getUrl()} className={styles.relatedResourceLink}>
                                            <h3>{related.title}</h3>
                                            <p className={styles.relatedResourceExcerpt}>
                                                {related.getTruncatedExcerpt(100)}
                                            </p>
                                            <div className={styles.relatedResourceMeta}>
                                                <span className={styles.relatedResourceCategory}>
                                                    {related.category.name}
                                                </span>
                                                <span className={styles.relatedResourceDate}>
                                                    {related.getFormattedUpdatedDate()}
                                                </span>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </article>
            </div>
        </>
    );
};

export default ResourceDetail;